import { Tab, Tabs, TabsList } from "@mui/base";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect, useMemo } from "react";
import {
  FormProvider,
  UseFormReturn,
  useForm,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AppConfig } from "~/AppConfig";
import { queryKeys } from "~/api/queryKeys";
import { useTeam } from "~/contexts/TeamProvider";
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  useFeatureFlagsConfig,
} from "~/featureFlags";
import { CRAWL_MODE } from "~/helpers/consts";
import { useSite } from "~/hooks/useSite";
import Whitelist from "~/pages/dashboard/Whitelist";

import { SiteUpdateAttributes } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import { uploadFile } from "../../api/apiFunctions";
import Button from "../mantine/button/Button";
import Spinner from "../ui/spinner/Spinner";
import AIEngineSettings from "./AIEngineSettings";
import CrawlerSettings from "./CrawlerSettings";
import General from "./General";
import { GoogleIntegration } from "./GoogleIntegration/GoogleIntegration";
import JavaScriptSnippetAutomation from "./JavaScriptSnippetAutomation";
import SchemaSettings from "./SchemaSettings";
import styles from "./SiteSettings.module.scss";

export interface SiteSettingsFormValues {
  name?: string;
  domain?: string;
  prerendering?: boolean;
  site_speed_requested?: boolean;
  apex_redirect?: boolean;
  inject_widget?: boolean;
  inject_markdown?: "hidden" | "visible" | "shortcut" | null;
  disable_defer_automation?: boolean;
  disable_lazy_automation?: boolean;
  disable_preload_automation?: boolean;
  crawl_frequency_override?: number | null;
  ai_title?: boolean;
  ai_description?: boolean;
  ai_schema?: boolean;
  ai_meta_override?: number;
  ai_schema_override?: number;
  page_max_override?: number | null;
  crawl_concurrency?: number;
  page_imports_attributes?: {
    input_file?: File | null | string;
  };
  crawl_mode?: string;
  schema_page_types?: string[] | null;
  waf_enabled?: SiteUpdateAttributes["waf_enabled"];
  waf_level?: SiteUpdateAttributes["waf_level"];
  waf_exclusions?: SiteUpdateAttributes["waf_exclusions"];
}

interface TabItem {
  id: string;
  value: number;
  label: string;
  title: string;
  description?: string;
  feature?: string;
  content: (methods: UseFormReturn<SiteSettingsFormValues>) => React.ReactNode;
}

const CONTENT_COMPARISON_FIELDS = new Set<keyof SiteSettingsFormValues>([
  "schema_page_types",
]);

function hasFormValueChanged(
  formKey: keyof SiteSettingsFormValues,
  formValue: SiteSettingsFormValues[keyof SiteSettingsFormValues],
  siteValue: unknown,
) {
  // Arrays and other structured values need content comparison,
  // otherwise identical values with different references look "changed".
  if (CONTENT_COMPARISON_FIELDS.has(formKey)) {
    return JSON.stringify(formValue) !== JSON.stringify(siteValue);
  }

  return formValue !== siteValue;
}

const SiteSettings = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();

  if (!id) {
    throw Error("No site id provided");
  }

  const location = useLocation();
  const navigate = useNavigate();
  const { team } = useTeam();
  const flags = useFeatureFlagsConfig();

  const { data: siteData, isLoading: siteDateIsLoading } = useSite(id);

  const methods = useForm<SiteSettingsFormValues>();

  useEffect(() => {
    const site = siteData?.site;

    if (!site) return;

    methods.reset({
      name: site.domain,
      domain: site.domain,
      crawl_mode: site.crawl_mode,
      crawl_frequency_override: site.crawl_frequency_override,
      ai_title: site.ai_title,
      ai_description: site.ai_description,
      ai_schema: site.ai_schema,
      ai_meta_override: site.ai_meta_override,
      ai_schema_override: site.ai_schema_override,
      page_max_override: site.page_max_override,
      crawl_concurrency: site.crawl_concurrency,
      schema_page_types: site.schema_page_types,
    });
  }, [siteData?.site]);

  const { data: planUsage, isLoading: planUsageIsLoading } = useQuery({
    queryKey: ["planUsage", id],
    queryFn: () => apiClient.teams.usageList(),
  });
  // null = no page cap (usage-billed plans); the generated type says number
  const pageMax: number | null =
    planUsage?.data?.team_usage?.team_limits?.page_max ?? null;

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const extractChangedValues = () => {
    const formData = methods.getValues();
    const changedValues: Partial<SiteUpdateAttributes> = {};

    Object.keys(formData).forEach((key) => {
      const formKey = key as keyof SiteSettingsFormValues;
      const siteKey = siteData?.site
        ? (key as keyof typeof siteData.site)
        : undefined;

      if (
        formKey === "page_imports_attributes" ||
        formKey === "name" ||
        formKey === "domain"
      ) {
        return;
      }

      const siteValue = siteKey ? siteData?.site?.[siteKey] : undefined;

      const formValue = formData[formKey];

      if (hasFormValueChanged(formKey, formValue, siteValue)) {
        //@ts-expect-error BE types
        changedValues[formKey] = formValue;
      }
    });

    return changedValues;
  };

  const handleSuccess = (message: string) => {
    enqueueSnackbar(message, { variant: "success" });
    queryClient.invalidateQueries({ queryKey: queryKeys.siteDetails(id) });
  };

  const handleError = (message: string) => {
    enqueueSnackbar(message, { variant: "error", persist: true });
  };

  const { mutate: updateSite, isPending: updateSiteIsPending } = useMutation({
    mutationFn: async (data: SiteUpdateAttributes) => {
      return apiClient.sites.sitesPartialUpdate(
        Number(siteData?.site.id),
        data,
      );
    },
    //@ts-expect-error BE types
    onError: ({ error }) => {
      const errorMessage =
        error?.error?.message || "Site settings are not updated!";
      handleError(errorMessage);
    },
    onSuccess: () => handleSuccess("Site settings updated successfully!"),
  });

  const { mutate: mutateFile, isPending: isPendingFile } = useMutation({
    mutationFn: ({
      file,
      collection,
      reflection,
    }: {
      file: File;
      collection?: string;
      reflection?: string;
    }) => uploadFile(file, collection, reflection),
    onError: () => handleError("File is not uploaded!"),
    onSuccess: async (response, { file }) => {
      const data =
        typeof response.json === "function" ? await response.json() : response;
      const { direct_upload } = data;
      if (!direct_upload?.url) return;
      const changedValues = extractChangedValues();

      try {
        await fetch(direct_upload.url, {
          method: "PUT",
          headers: direct_upload.headers,
          body: file,
        });
        //must be after file upload to S3
        updateSite({
          ...changedValues,
          page_imports_attributes: [{ input_file: data.signed_id }],
        });
        enqueueSnackbar("File uploaded successfully", {
          variant: "success",
        });
      } catch {
        enqueueSnackbar("Error uploading file", {
          variant: "error",
          persist: true,
        });
      }
    },
  });
  const crawlModeFromForm = useWatch({
    control: methods.control,
    name: "crawl_mode",
    defaultValue: siteData?.site?.crawl_mode,
  });
  const shouldShowWhitelistPage = crawlModeFromForm === CRAWL_MODE.WHITELISTED;
  const shouldShowAiSettings = team?.ai_engine_available;
  const shouldShowSchemaSettings = team?.ai_schema_available;

  const isLoading = siteDateIsLoading || planUsageIsLoading;

  const rawTabs: TabItem[] = useMemo(() => {
    return [
      {
        id: "general",
        value: 0,
        label: "General",
        title: "General",
        feature: FEATURE_FLAGS.SITES_SETTINGS_GENERAL,
        content: () => <General siteData={siteData} isLoading={isLoading} />,
      },

      ...(shouldShowAiSettings
        ? [
            {
              id: "ai",
              value: 2,
              label: "AI Meta",
              title: "AI Page Meta Content Generation Settings",
              feature: FEATURE_FLAGS.SITES_SETTINGS_AI,
              content: () => (
                <AIEngineSettings
                  siteData={siteData}
                  isLoading={isLoading}
                  pageMax={pageMax}
                />
              ),
            },
          ]
        : []),

      ...(shouldShowSchemaSettings
        ? [
            {
              id: "schema",
              value: 3,
              label: "AI Schema",
              title: "AI JSON Schema Generation Settings",
              feature: FEATURE_FLAGS.SITES_SETTINGS_JSON_SCHEMA,
              content: () => (
                <SchemaSettings
                  siteData={siteData}
                  isLoading={isLoading}
                  pageMax={pageMax}
                />
              ),
            },
          ]
        : []),

      {
        id: "snippet",
        value: 4,
        label: "JS Snippet",
        title: "JavaScript Snippet for SEO Automation",
        feature: FEATURE_FLAGS.SITES_SETTINGS_JS_SNIPPET,
        content: () => (
          <JavaScriptSnippetAutomation
            siteData={siteData}
            isLoading={isLoading}
          />
        ),
      },

      {
        id: "crawler",
        value: 5,
        label: "Crawler",
        title: "Crawler Settings",
        feature: FEATURE_FLAGS.SITES_SETTINGS_CRAWLER,
        content: () => (
          <CrawlerSettings
            siteData={siteData}
            isLoading={isLoading}
            pageMax={pageMax}
          />
        ),
      },

      {
        id: "integrations",
        value: 6,
        label: "Integrations",
        title: "Integrations",
        feature: FEATURE_FLAGS.SITES_SETTINGS_GOOGLE_INTEGRATIONS,
        content: () => (
          <GoogleOAuthProvider clientId={AppConfig.GOOGLE_ANALYTICS_CLIENT_ID}>
            <GoogleIntegration />
          </GoogleOAuthProvider>
        ),
      },

      ...(shouldShowWhitelistPage
        ? [
            {
              id: "whitelist",
              value: 7,
              label: "Whitelist",
              title: "Whitelist",
              feature: FEATURE_FLAGS.SITES_SETTINGS_WHITELIST,
              content: () => (
                <Whitelist siteData={siteData} isLoading={isLoading} />
              ),
            },
          ]
        : []),
    ];
  }, [
    siteData,
    isLoading,
    pageMax,
    shouldShowWhitelistPage,
    shouldShowAiSettings,
    shouldShowSchemaSettings,
  ]);

  const tabs = useMemo(() => {
    if (!flags) return rawTabs;

    return rawTabs.filter(
      (t) => !t.feature || isFeatureFlagEnabled(flags, t.feature),
    );
  }, [rawTabs, flags]);

  const currentPath = location.pathname;
  const currentTabId = currentPath.split("/").pop();
  const activeTab =
    tabs.find((tab) => tab.id === currentTabId)?.value ?? tabs[0]?.value ?? 0;
  const currentTab = tabs.find((tab) => tab.id === currentTabId) ?? tabs[0];

  const handleTabChange = (
    _event: React.SyntheticEvent | null,
    newValue: string | number | null,
  ) => {
    const selectedTab = tabs.find((tab) => tab.value === newValue);
    if (selectedTab) {
      if (selectedTab.id === "general") {
        navigate(`/sites/${id}/settings/`);
      } else {
        navigate(`/sites/${id}/settings/${selectedTab.id}`);
      }
    }
  };

  const onSubmit = async (formData: SiteSettingsFormValues) => {
    if (
      formData.crawl_mode === CRAWL_MODE.WHITELISTED &&
      !formData.page_imports_attributes?.input_file &&
      // @ts-expect-error BE types
      !siteData?.site?.page_import?.input_file
    ) {
      enqueueSnackbar(
        "A CSV file with URLs is required when using whitelist mode",
        {
          variant: "error",
          persist: true,
        },
      );

      return;
    }

    const file = formData.page_imports_attributes?.input_file;

    if (file && file instanceof File) {
      mutateFile({
        file,
        collection: "page_imports",
        reflection: "input_file",
      });
    }

    if (!file || !(file instanceof File)) {
      const changedValues = extractChangedValues();

      if (Object.keys(changedValues).length > 0) {
        updateSite(changedValues as SiteUpdateAttributes);
      }
    }
  };

  const handleReset = () => {
    const originalSchemaTypes = siteData?.site?.schema_page_types || [];

    methods.reset({
      name: siteData?.site?.fqdn,
      domain: siteData?.site?.fqdn,
      crawl_mode: siteData?.site?.crawl_mode,
      crawl_frequency_override: siteData?.site?.crawl_frequency_override,
      ai_title: siteData?.site?.ai_title,
      ai_description: siteData?.site?.ai_description,
      ai_schema: siteData?.site?.ai_schema,
      ai_meta_override: siteData?.site?.ai_meta_override,
      ai_schema_override: siteData?.site?.ai_schema_override,
      page_max_override: siteData?.site?.page_max_override,
      crawl_concurrency: siteData?.site?.crawl_concurrency,
      schema_page_types: originalSchemaTypes,
    });
  };

  const isPending = isPendingFile || updateSiteIsPending;

  return (
    <main className={styles.settings}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {/*
             // @ts-expect-error TS2551 non-existing className */}
            <div className={styles.settings__content}>
              {/*
               // @ts-expect-error TS2551 non-existing className */}
              <nav className={styles.settings__tabs}>
                <TabsList className={styles.settingsTabsList}>
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      // @ts-expect-error TS2551 non-existing className
                      className={styles.settings__tab}
                      value={tab.value}
                    >
                      {tab.label}
                    </Tab>
                  ))}
                </TabsList>
              </nav>

              {currentTab && (
                // @ts-expect-error TS2551 non-existing className
                <div className={styles.settings__panel}>
                  <section className={styles.settingsSection}>
                    <h2 className={styles.settingsSectionTitle}>
                      {currentTab.title}
                    </h2>
                  </section>
                  {currentTab.content(methods)}
                </div>
              )}
            </div>
          </Tabs>

          <div className={styles.settingsFooter}>
            <div className={styles.settingsFooterButtons}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleReset}
                disabled={!isDirty}
              >
                Revert
              </Button>
              <Button type="submit" disabled={!isDirty || isPending}>
                {isPending && <Spinner size="1rem" />}
                Save Changes
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </main>
  );
};

export default SiteSettings;
